/**
 * Whether this device has ever held a session (`D262`) — the one thing the
 * gate reads to choose, for a person with no session, between the intake and
 * sign-in.
 *
 * ── WHY A KEY OF ITS OWN ───────────────────────────────────────────────────
 * The account scope (`data/account.ts`) is not this answer: a deliberate
 * sign-out forgets it, and a person who just signed out is the person most
 * likely to sign back in. This key belongs to the DEVICE, not to an account,
 * so no sign-out and no account cleanup removes it. It holds no identity —
 * only that somebody signed in here once.
 *
 * Written whenever a session exists, so an install signed in before this key
 * existed learns it on its first launch of a bundle that knows it.
 *
 * Native-free, over the injected `KeyValueStorage`, so it runs in Node.
 */
import type { KeyValueStorage } from "@repo/client/i18n";

export const SIGNED_IN_BEFORE_KEY = "mypocketgym.auth.signed-in-before";

const YES = "1";

/** `false` when nothing is stored, when anything else is, or when the read
 *  fails: the intake, with its "Já tenho conta" door, is the safe default. */
export async function readSignedInBefore(
  storage: KeyValueStorage
): Promise<boolean> {
  try {
    return (await storage.getItem(SIGNED_IN_BEFORE_KEY)) === YES;
  } catch {
    return false;
  }
}

/** Never throws: a lost write costs the next sign-out one tap on the door. */
export async function rememberSignedIn(
  storage: KeyValueStorage
): Promise<void> {
  try {
    await storage.setItem(SIGNED_IN_BEFORE_KEY, YES);
  } catch {
    // As above.
  }
}
